/**
 * The reconciliation's own row shape: one row per tenancy.
 *
 * This is the shape the response actually has (`tenancies[]`), so every figure in a
 * row is a field the backend set — no column is computed here. In particular there
 * is no "days overdue" column: the due/not-due split lives in the two open columns
 * the service filled using ITS `today`, and a client-side day count would be a
 * second clock that can disagree with them.
 *
 * Money is rendered with the response's own currency and keeps its sign: a negative
 * remaining is a possible overpayment, which the service never clamps.
 */
import React from "react";
import { AlertTriangle } from "lucide-react";

import { fmtMoney } from "../bexioFormat";

/** A value the backend could not resolve stays visibly absent. */
const DASH = "—";

function Identity({ row }) {
  const ref = row.tenancy_reference_number || DASH;
  const sub = [row.unit_label, row.room_label].filter(Boolean).join(" · ");
  return (
    <>
      <span className="block truncate font-medium text-foreground">{ref}</span>
      <span className="block truncate text-xs text-muted-foreground">
        {sub || DASH}
      </span>
    </>
  );
}

export default function BexioTenancyTable({ rows, currency, hasHint, onSelect }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-border text-left">
            <th
              scope="col"
              className="w-[24%] px-4 py-2.5 text-xs font-medium text-muted-foreground"
            >
              Mietverhältnis / Objekt
            </th>
            <th
              scope="col"
              className="w-[18%] px-4 py-2.5 text-xs font-medium text-muted-foreground"
            >
              Mieter
            </th>
            <th
              scope="col"
              className="w-[8%] whitespace-nowrap px-4 py-2.5 text-right text-xs font-medium text-muted-foreground"
            >
              Rechnungen
            </th>
            <th
              scope="col"
              className="w-[12%] whitespace-nowrap px-4 py-2.5 text-right text-xs font-medium text-muted-foreground"
            >
              Fakturiert
            </th>
            <th
              scope="col"
              className="w-[12%] whitespace-nowrap px-4 py-2.5 text-right text-xs font-medium text-muted-foreground"
            >
              Bezahlt
            </th>
            <th
              scope="col"
              className="w-[12%] whitespace-nowrap px-4 py-2.5 text-right text-xs font-medium text-muted-foreground"
            >
              Offen (fällig)
            </th>
            {/* Hidden on narrow screens rather than dropped: the drawer carries it
                for every row, so nothing becomes unreachable. */}
            <th
              scope="col"
              className="hidden w-[12%] whitespace-nowrap px-4 py-2.5 text-right text-xs font-medium text-muted-foreground xl:table-cell"
            >
              Offen (nicht fällig)
            </th>
            <th
              scope="col"
              className="w-[12%] whitespace-nowrap px-4 py-2.5 text-right text-xs font-medium text-muted-foreground"
            >
              Offen gesamt
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.tenancy_id}
              className="border-b border-border last:border-0 hover:bg-muted/50"
            >
              <td className="px-4 py-2.5">
                <button
                  type="button"
                  onClick={() => onSelect(r)}
                  aria-label={`Details zu ${
                    r.tenancy_reference_number ||
                    r.tenant_display_name ||
                    `Mietverhältnis ${r.tenancy_id}`
                  } anzeigen`}
                  className="block max-w-full rounded text-left text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <Identity row={r} />
                </button>
              </td>
              <td className="max-w-0 truncate px-4 py-2.5 text-muted-foreground">
                <span className="inline-flex max-w-full items-center gap-1.5">
                  <span className="truncate">{r.tenant_display_name || DASH}</span>
                  {hasHint(r) ? (
                    <AlertTriangle
                      className="h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400"
                      aria-label="Hinweis vorhanden"
                    />
                  ) : null}
                </span>
              </td>
              <td className="whitespace-nowrap px-4 py-2.5 text-right tabular-nums text-muted-foreground">
                {r.counted_invoices}
              </td>
              <td className="whitespace-nowrap px-4 py-2.5 text-right tabular-nums text-foreground">
                {fmtMoney(r.invoice_total_counted, currency)}
              </td>
              <td className="whitespace-nowrap px-4 py-2.5 text-right tabular-nums text-foreground">
                {fmtMoney(r.paid_total_invoice_level, currency)}
              </td>
              <td
                className={`whitespace-nowrap px-4 py-2.5 text-right tabular-nums ${
                  Number(r.open_due || 0) !== 0
                    ? "font-medium text-red-700 dark:text-red-300"
                    : "text-muted-foreground"
                }`}
              >
                {fmtMoney(r.open_due, currency)}
              </td>
              <td className="hidden whitespace-nowrap px-4 py-2.5 text-right tabular-nums text-muted-foreground xl:table-cell">
                {fmtMoney(r.open_not_yet_due_or_no_due_date, currency)}
              </td>
              <td className="whitespace-nowrap px-4 py-2.5 text-right font-medium tabular-nums text-foreground">
                {fmtMoney(r.open_total, currency)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
